import { AppDispatch } from 'client/store'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { BorderType } from 'common/types/styleTypes'
import {
  WebsiteButtonsInterface,
  WebsiteFontSettingsInterface,
  WebsiteFormFieldsSettings,
  WebsiteLayoutSettings,
  WebsitePageDataInterface,
  WebsiteSettingsInterface,
  WebsiteSettingsTypographyTypeEnum,
  WebsiteStylesInterface,
  WebsiteThemeInterface,
  WebsiteThemeListInterface,
  WebsiteTypographySettingsStylesInterface,
} from '../../../shared/types/website-settings-types'
import {
  SET_CURRENT_WEBSITE_LOCALE,
  SET_WEBSITE_DRAFT_THEME,
  SET_WEBSITE_LOCALE,
  SET_WEBSITE_LOCALES,
  SET_WEBSITE_NAME,
  SET_WEBSITE_SETTINGS,
  SET_WEBSITE_STYLES,
  SET_WEBSITE_THEME,
  SET_WEBSITE_THEMES_LIST,
  UPDATE_WEBSITE_STYLES_FORM_FIELD,
  UPDATE_WEBSITE_STYLES_FORM_FIELD_BORDER,
  UPDATE_WEBSITE_STYLES_FORM_FIELD_TYPOGRAPHY,
  UPDATE_WEBSITE_STYLES_LAYOUT_SETTINGS,
  UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS,
  UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS_BORDER,
  UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS_TYPOGRAPHY,
  UPDATE_WEBSITE_STYLES_SECONDARY_BUTTONS,
  UPDATE_WEBSITE_STYLES_SECONDARY_BUTTONS_BORDER,
  UPDATE_WEBSITE_STYLES_SECONDARY_BUTTONS_TYPOGRAPHY,
  UPDATE_WEBSITE_STYLES_TYPOGRAPHY_HEADING,
  UPDATE_WEBSITE_STYLES_TYPOGRAPHY_PARAGRAPH,
  UPDATE_WEBSITE_THEME_LINKS,
} from './website-settings-action-type'
import {
  WebsiteDraftThemeAction,
  WebsiteLocaleAction,
  WebsiteLocalesAction,
  WebsiteNameAction,
  WebsiteSettingsAction,
  WebsiteStylesAction,
  WebsiteStylesFormFieldAction,
  WebsiteStylesFormFieldBorderAction,
  WebsiteStylesFormFieldTypographyAction,
  WebsiteStylesPrimaryButtonsAction,
  WebsiteStylesPrimaryButtonsBorderAction,
  WebsiteStylesPrimaryButtonsTypographyAction,
  WebsiteStylesSecondaryButtonsAction,
  WebsiteStylesSecondaryButtonsBorderAction,
  WebsiteStylesSecondaryButtonsTypographyAction,
  WebsiteStylesTypographyHeadingAction,
  WebsiteStylesTypographyParagraphAction,
  WebsiteThemeAction,
  WebsiteThemeLinksAction,
  WebsiteThemeListAction,
  WebsiteUpdateStylesLayoutAction,
  WebsiteCurrentLocaleAction,
} from './website-settings-reducer'

export const setWebsiteStyles = (
  styles: WebsiteStylesInterface,
): WebsiteStylesAction => ({
  type: SET_WEBSITE_STYLES,
  payload: styles,
})

export const updateWebsiteStylesParagraphTypography = <
  T extends WebsiteTypographySettingsStylesInterface,
>(
  typography: T,
): WebsiteStylesTypographyParagraphAction<T> => ({
  type: UPDATE_WEBSITE_STYLES_TYPOGRAPHY_PARAGRAPH,
  payload: typography,
})

export const updateWebsiteStylesHeadingTypography = <
  T extends WebsiteTypographySettingsStylesInterface,
>(
  typography: T,
): WebsiteStylesTypographyHeadingAction<T> => ({
  type: UPDATE_WEBSITE_STYLES_TYPOGRAPHY_HEADING,
  payload: typography,
})

export const updateWebsiteStylesPrimaryButtonsTypography = <
  T extends WebsiteFontSettingsInterface,
>(
  typography: T,
): WebsiteStylesPrimaryButtonsTypographyAction<T> => ({
  type: UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS_TYPOGRAPHY,
  payload: typography,
})

export const updateWebsiteStylesFormFieldTypography = <
  T extends WebsiteFontSettingsInterface,
>(
  typography: T,
): WebsiteStylesFormFieldTypographyAction<T> => ({
  type: UPDATE_WEBSITE_STYLES_FORM_FIELD_TYPOGRAPHY,
  payload: typography,
})

export const updateWebsiteStylesSecondaryButtonsTypography = <
  T extends WebsiteFontSettingsInterface,
>(
  typography: T,
): WebsiteStylesSecondaryButtonsTypographyAction<T> => ({
  type: UPDATE_WEBSITE_STYLES_SECONDARY_BUTTONS_TYPOGRAPHY,
  payload: typography,
})

export const updateWebsiteStylesPrimaryButtons = (
  primary: WebsiteButtonsInterface,
): WebsiteStylesPrimaryButtonsAction => ({
  type: UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS,
  payload: primary,
})

export const updateWebsiteStylesSecondaryButtons = (
  secondary: WebsiteButtonsInterface,
): WebsiteStylesSecondaryButtonsAction => ({
  type: UPDATE_WEBSITE_STYLES_SECONDARY_BUTTONS,
  payload: secondary,
})

export const updateWebsiteStylesPrimaryButtonsBorder = (
  border: Partial<BorderType>,
): WebsiteStylesPrimaryButtonsBorderAction<Partial<BorderType>> => ({
  type: UPDATE_WEBSITE_STYLES_PRIMARY_BUTTONS_BORDER,
  payload: border,
})

export const updateWebsiteStylesSecondaryButtonsBorder = (
  border: Partial<BorderType>,
): WebsiteStylesSecondaryButtonsBorderAction<Partial<BorderType>> => ({
  type: UPDATE_WEBSITE_STYLES_SECONDARY_BUTTONS_BORDER,
  payload: border,
})

export const updateWebsiteStylesFormFieldBorder = (
  border: Partial<BorderType>,
): WebsiteStylesFormFieldBorderAction<Partial<BorderType>> => ({
  type: UPDATE_WEBSITE_STYLES_FORM_FIELD_BORDER,
  payload: border,
})

export const updateWebsiteStylesFormField = (
  formFieldStyle: WebsiteFormFieldsSettings,
): WebsiteStylesFormFieldAction => ({
  type: UPDATE_WEBSITE_STYLES_FORM_FIELD,
  payload: formFieldStyle,
})

export const updateWebsiteThemeLinks = (
  links: WebsiteSettingsInterface['theme']['links'],
): WebsiteThemeLinksAction => ({
  type: UPDATE_WEBSITE_THEME_LINKS,
  payload: links,
})

export const updateWebsiteStylesLayout = (
  websiteLayoutSettings: Partial<WebsiteLayoutSettings>,
): WebsiteUpdateStylesLayoutAction => ({
  type: UPDATE_WEBSITE_STYLES_LAYOUT_SETTINGS,
  payload: websiteLayoutSettings,
})

export const setWebsiteName = (name: string): WebsiteNameAction => ({
  type: SET_WEBSITE_NAME,
  payload: name,
})

export const setCurrentWebsiteLocale = (
  locale: PageLocaleEnum,
): WebsiteCurrentLocaleAction => ({
  type: SET_CURRENT_WEBSITE_LOCALE,
  payload: locale,
})

export const setWebsiteLocale = (
  locale: PageLocaleEnum,
): WebsiteLocaleAction => ({
  type: SET_WEBSITE_LOCALE,
  payload: locale,
})

export const setWebsiteLocales = (
  locales: PageLocaleEnum[],
): WebsiteLocalesAction => ({
  type: SET_WEBSITE_LOCALES,
  payload: locales,
})

export const setWebsiteTheme = (
  theme: WebsiteThemeInterface,
): WebsiteThemeAction => ({
  type: SET_WEBSITE_THEME,
  payload: theme,
})

export const setWebsiteDraftTheme = (
  theme: WebsiteThemeInterface | null,
): WebsiteDraftThemeAction => ({
  type: SET_WEBSITE_DRAFT_THEME,
  payload: theme,
})

export const setWebsiteThemesList = (
  themes: WebsiteThemeListInterface[],
): WebsiteThemeListAction => ({
  type: SET_WEBSITE_THEMES_LIST,
  payload: themes,
})

export const setWebsiteSettings = (
  website: WebsitePageDataInterface,
): WebsiteSettingsAction => ({
  type: SET_WEBSITE_SETTINGS,
  payload: website,
})

export const fetchWebsiteDraftTheme =
  (theme: WebsiteThemeInterface) => async (dispatch: AppDispatch) => {
    dispatch(setWebsiteDraftTheme(theme))
  }

export const buttonStyleUpdaters = {
  [WebsiteSettingsTypographyTypeEnum.PrimaryButton]: {
    updateWebsiteStylesButtonsTypography:
      updateWebsiteStylesPrimaryButtonsTypography,
    updateWebsiteStylesButtonsBorder: updateWebsiteStylesPrimaryButtonsBorder,
  },
  [WebsiteSettingsTypographyTypeEnum.SecondaryButton]: {
    updateWebsiteStylesButtonsTypography:
      updateWebsiteStylesSecondaryButtonsTypography,
    updateWebsiteStylesButtonsBorder: updateWebsiteStylesSecondaryButtonsBorder,
  },
}
